import { AuthTokens } from './auth';

const syncKey = 'agent_backoffice_auth_sync_access_token';

export type Pagination = {
  total?: number;
  limit?: number;
  offset?: number;
};

export type Team = {
  teamId: string;
  name: string;
  description?: string;
  status: TeamStatus | string;
  installationId?: number;
  allowedRepos?: string[];
  jiraProjects?: JiraProject[];
  metadata?: {
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
  };
};

export type TeamListResponse = {
  data?: Team[];
  pagination?: Pagination;
};

export type TeamResponse = {
  data?: Team;
};

export type TeamStatus = 'active' | 'inactive' | 'suspended';

export type JiraProject = {
  projectKey: string;
  agentId: string;
  description?: string;
  status: 'active' | 'revoked' | string;
  createdAt: string;
  createdBy?: string;
  revokedAt?: string;
};

export type WebhookKeyListResponse = {
  data?: JiraProject[];
};

export type WebhookKeyResponse = {
  data?: JiraProject;
};

export type CreateWebhookKeyRequest = {
  projectKey: string;
  agentId: string;
  description?: string;
};

export type WebhookKeyCreatedResponse = {
  data?: {
    token: string;
    projectKey: string;
    agentId: string;
    issuedAt?: string;
  };
};

export type UpdateTeamRequest = {
  name?: string;
  description?: string;
  status?: TeamStatus;
};

export type CreateTeamRequest = {
  name: string;
  description?: string;
};

export type InstallTeamRequest = {
  installationId: number;
};

export type Agent = {
  agentId: string;
  name: string;
  description?: string;
};

export type AgentListResponse = {
  data?: Agent[];
  pagination?: Pagination;
};

export type AgentTemplate = {
  templateId: string;
  name: string;
  description?: string;
};

export type AgentTemplateListResponse = {
  data?: AgentTemplate[];
  pagination?: Pagination;
};

export type Skill = {
  skillId: string;
  name: string;
  description?: string;
};

export type SkillListResponse = {
  data?: Skill[];
  pagination?: Pagination;
};

export type Environment = {
  environmentId: string;
  name: string;
  versions: string[];
};

export type EnvironmentListResponse = {
  data?: Environment[];
};

export type GitHubRepository = {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  node_id?: string;
};

export type UnclaimedInstallation = {
  installationId: number;
  accountLogin: string;
  accountId: number;
  accountType?: 'User' | 'Organization' | string;
  repositories?: GitHubRepository[];
  createdAt?: string;
};

export type UnclaimedInstallationListResponse = {
  data?: UnclaimedInstallation[];
  pagination?: Pagination;
};

export type JobStatus = 'QUEUED' | 'CANCELLED' | 'STARTING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'PURGED';

export type Job = {
  requestId: string;
  agentId?: string;
  status?: JobStatus | string;
  initialPrompt?: string;
  email?: string;
  createdAt?: string;
  updatedAt?: string;
  installationId?: number;
  teamId?: string;
  userId?: string;
};

export type JobListResponse = {
  data?: Job[];
  pagination?: Pagination;
};

export type FesConfig = {
  baseUrl: string;
};

function assertFesConfig(config: FesConfig): void {
  if (!config.baseUrl) {
    throw new Error('Missing FES config: REACT_APP_FES_BASE_URL');
  }
}

async function fetchJson(config: FesConfig, path: string, tokens: AuthTokens, init?: RequestInit): Promise<unknown> {
  assertFesConfig(config);

  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${tokens.accessToken}`);

  const response = await window.fetch(new URL(path, config.baseUrl).toString(), {
    ...init,
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    let errorMessage = errorBody;

    try {
      const parsedError = JSON.parse(errorBody);
      errorMessage = parsedError?.error?.message || parsedError?.message || errorBody;
    } catch {
      errorMessage = errorBody;
    }

    throw new Error(errorMessage || `FES request failed (${response.status}) for ${path}`);
  }

  const body = await response.text();

  if (response.status === 204 || !body) {
    return null;
  }

  return JSON.parse(body);
}

export async function syncAuthenticatedUser(config: FesConfig, tokens: AuthTokens): Promise<void> {
  if (window.sessionStorage.getItem(syncKey) === tokens.accessToken) {
    return;
  }

  await fetchJson(config, '/auth/sync', tokens, {
    method: 'POST',
  });

  window.sessionStorage.setItem(syncKey, tokens.accessToken);
}

export async function getTeams(config: FesConfig, tokens: AuthTokens): Promise<TeamListResponse> {
  return fetchJson(config, '/config-service/teams', tokens) as Promise<TeamListResponse>;
}

export async function getTeam(config: FesConfig, tokens: AuthTokens, teamId: string): Promise<TeamResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}`, tokens) as Promise<TeamResponse>;
}

export async function createTeam(config: FesConfig, tokens: AuthTokens, team: CreateTeamRequest): Promise<TeamResponse> {
  return fetchJson(config, '/config-service/teams', tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(team),
  }) as Promise<TeamResponse>;
}

export async function getWebhookKeys(config: FesConfig, tokens: AuthTokens, teamId: string): Promise<WebhookKeyListResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/webhook-keys`, tokens) as Promise<WebhookKeyListResponse>;
}

export async function createWebhookKey(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  webhookKey: CreateWebhookKeyRequest
): Promise<WebhookKeyCreatedResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/webhook-keys`, tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(webhookKey),
  }) as Promise<WebhookKeyCreatedResponse>;
}

export async function deleteWebhookKey(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  projectKey: string
): Promise<WebhookKeyResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/webhook-keys/${encodeURIComponent(projectKey)}`, tokens, {
    method: 'DELETE',
  }) as Promise<WebhookKeyResponse>;
}

export async function updateTeam(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  team: UpdateTeamRequest
): Promise<TeamResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}`, tokens, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(team),
  }) as Promise<TeamResponse>;
}

export async function installTeam(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  installRequest: InstallTeamRequest
): Promise<TeamResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/install`, tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(installRequest),
  }) as Promise<TeamResponse>;
}

export async function getAgents(config: FesConfig, tokens: AuthTokens): Promise<AgentListResponse> {
  return fetchJson(config, '/config-service/agents', tokens) as Promise<AgentListResponse>;
}

export async function getTemplates(config: FesConfig, tokens: AuthTokens): Promise<AgentTemplateListResponse> {
  return fetchJson(config, '/config-service/templates', tokens) as Promise<AgentTemplateListResponse>;
}

export async function getSkills(config: FesConfig, tokens: AuthTokens): Promise<SkillListResponse> {
  return fetchJson(config, '/config-service/skills', tokens) as Promise<SkillListResponse>;
}

export async function getEnvironments(config: FesConfig, tokens: AuthTokens): Promise<EnvironmentListResponse> {
  return fetchJson(config, '/config-service/environments', tokens) as Promise<EnvironmentListResponse>;
}

export async function getUnclaimedInstallations(
  config: FesConfig,
  tokens: AuthTokens
): Promise<UnclaimedInstallationListResponse> {
  return fetchJson(config, '/config-service/unclaimed-installations', tokens) as Promise<UnclaimedInstallationListResponse>;
}

export const JOBS_PAGE_SIZE = 20;

export async function getJobs(
  config: FesConfig,
  tokens: AuthTokens,
  params: { limit?: number; offset?: number } = {}
): Promise<JobListResponse> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? JOBS_PAGE_SIZE),
    offset: String(params.offset ?? 0),
  });

  return fetchJson(config, `/job-service/jobs?${query.toString()}`, tokens) as Promise<JobListResponse>;
}
